import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import StyleSheet from 'styles'
import { withRouter } from 'react-router'
import QRCode from 'qrcode.react'

import Typography from '@material-ui/core/Typography'
import ReplayIcon from '@material-ui/icons/Replay'
import Button from '@material-ui/core/Button'

import FormMixin from 'containers/shared/FormMixin'
import { DealSheetActions, ThemeActions, SnackbarActions } from 'actionsets'
import { Authorization, timeout } from 'utils'
import * as API from 'api'

const REFRESH_INTERVAL = parseInt(process.env.REACT_APP_WALLET_REFRESH_INTERVAL, 10) || 30000

export class Admin extends FormMixin(Component) {
  static propTypes = {
    dealSheet: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props)
    DealSheetActions.bindActions(this)
    ThemeActions.bindActions(this, 'theme')
    SnackbarActions.bindActions(this, 'snackbar')
    this.state = { ...super.status, walletInitEnabled: true }
  }

  componentDidMount = () => {
    this._isMounted = true
    this.poll()
    this.actions.theme.index()
  }

  componentWillUnmount = () => {
    this._isMounted = false
  }

  poll = async () => {
    if(this._isMounted) {
      await this.refreshWallet()
      await timeout(REFRESH_INTERVAL)
      this.poll()
    }
  }

  refreshWallet = async () => {
    if(Authorization.admin) {
      this.actions.loadWallet()
    }
  }

  startWalletInitEnabledTimer = async () => {
    if(!this._isMounted) return

    await timeout(60000)
    if(this._isMounted) {
      this.setState({walletInitEnabled: true})
    }
  }

  get paymentDestination(){
    return this.props.dealSheet.paymentDestination
  }

  get formObjectName(){
    return 'Deal Sheet'
  }

  get formObject(){
    return this.props.dealSheet
  }

  get createMode(){
    return false
  }

  get onSaveRoute(){
    return this.props.history.location.pathname
  }

  get canSave(){
    // TODO we should probably check if any of the input fields are editable
    // return this.props.dealSheet.currency === 'BTC' || this.wallet.status !== 'ready'
    return true;
  }

  get canSubmitToPayToX(){
    return (this.paymentDestination.status === 'unsubmitted')
  }

  handleTriggerWalletInit = async () => {
    this.setState({ walletInitEnabled: false})

    try {
      await API.Wallets.addressPoolInit({id: this.paymentDestination.id})
      this.startWalletInitEnabledTimer()
    } catch(_) {
      this.actions.snackbar.error('Failed to trigger wallet initialisation!')
      this.setState({ walletInitEnabled: true})
    }
  }

  createWallet = async () => {
    let formObject = this.updatedFormObject

    this.saveObject(formObject, async () => {
      await this.saveAction(formObject)
      await this.actions.createWalletEvent(this.paymentDestination.id, { name: 'submit_to_pay_to_x' })
    })
  }

  renderWalletFields = () => {
    const editable = (this.paymentDestination.status === 'unsubmitted')
    const { WalletMNField, TextField, CheckboxField } = this
    let fields = []

    if(this.paymentDestination.isShared) {
      fields.push (
        <WalletMNField key='mn' label='Required signatures' disabled={!editable}/>,
        <TextField key='invitation' attribute='paymentDestination.invitation' label='Wallet invitation' disabled={true}/>
      )
    }

    if(this.props.dealSheet.currency.code === 'ETH') {
      fields.push(
        <TextField key='collectorAddress' attribute='paymentDestination.collectorAddress' label='ETH Collector address' required={true} disabled={!editable}/>,
        <TextField key='addressPoolSize' attribute='paymentDestination.addressPoolSize' label='Size of proxy address pool' required={true} disabled={!editable}/>
      )
    }

    return (
      <Fragment>
        { fields }
        <CheckboxField key='auto' label='Automatically publish Deal Sheet when wallet ready?' attribute='paymentDestination.dealSheetAutoPublish' disabled={ this.paymentDestination.status === 'ready' }/>
      </Fragment>
    )
  }

  renderWalletStatus = () => {
    const { paymentDestination, currency } = this.props.dealSheet
    let content = []

    if(paymentDestination.isShared) {
      content.push(
        <div key='btc'>
          {
            (!!this.paymentDestination.invitation) &&
            <div style={{margin: '0px auto', width: 64, padding: '9px 9px 5px 9px', backgroundColor: '#FFF'}}>
              <QRCode value={this.paymentDestination.invitation} size={64} />
            </div>
          }
          {
            (paymentDestination.status === 'pending') &&
            <Typography variant='subtitle1'>{`Wallet was signed by ${paymentDestination.copayers.length} out of ${paymentDestination.n} so far.`}</Typography>
          }
          {
            (paymentDestination.status === 'ready') &&
            <Typography className={styles.body}>This wallet is ready to be used.</Typography>
          }
        </div>
      )

      if(this.paymentDestination.copayers.length !== 0) {
        content.push(
          <Fragment key='btc-copayers'>
            <Typography className={styles.subheading}>Signatories:</Typography>
            {
              this.paymentDestination.copayers.map((copayer) => {
                const { id, name } = copayer
                return <Typography key={id}>{ name }</Typography>
              })
            }
          </Fragment>
        )
      }
    } else if(currency.code === 'ETH') {
      let kovanTrackingAref = <a href={paymentDestination.operatingAddressUrl} target='_blank' rel='noopener noreferrer'><strong>{paymentDestination.operatingAddress}</strong></a>

      if(paymentDestination.status === 'pending') {
        content.push(
          <Fragment key='rth-pending'>
            <Typography paragraph className={styles.body}>The wallet is initializing ...</Typography>
            <Typography className={styles.body}>
            Please make sure that the operating address: {kovanTrackingAref} is properly funded.
            </Typography>
            <Typography className={styles.body}>
              The process of finalizing the wallet may take a little while so please be patient.
            </Typography>
            <Typography paragraph className={styles.body}>
              We'll update this screen as soon as the wallet is ready.
            </Typography>
            <Typography className={styles.body}>
              If for any reason you think that the wallet setup process has failed, please try to trigger it manually by pressing this button
              <Button style={{marginLeft: '10px'}} variant="fab" color="primary" mini aria-label="restart wallet initialisation"
                disabled={!this.state.walletInitEnabled} onClick={this.handleTriggerWalletInit}><ReplayIcon /></Button>
            </Typography>
          </Fragment>
        )
      } else if(paymentDestination.status === 'ready') {
        content.push(
          <Fragment key='eth-ready'>
            <Typography className={styles.body}>This wallet is ready to be used.</Typography>
            <Typography className={styles.body}>
              You can check the balance of the operating address by following this link {kovanTrackingAref}.
            </Typography>
          </Fragment>
        )
      }
    }

    if(content.length !== 0) {
      return (
        <div className={styles.walletStatus}>
          { content }
        </div>
      )
    }

    return null
  }

  renderCreateWalletButton = () => {
    return(
      <Button onClick={this.createWallet} className={styles.action} variant='contained' color='primary'>
        Create wallet
      </Button>
    )
  }

  renderWallet = () => {
    return(
      <Fragment>
        { this.renderWalletFields() }
        { this.renderWalletStatus() }
      </Fragment>
    )
  }

  renderPublicSwitch = () => {
    const { CheckboxField } = this
    return(
      <div>
        <CheckboxField attribute='visibility' label='Public (visible to non-logged-in users on the public carousel)'
          mapCheckedToValue={ checked => checked ? 'public' : 'private' }
          mapValueToChecked={ value => value === 'public' }
        />
      </div>
    )
  }

  renderThemeSelector = () => {
    const options = this.props.themes.map(({id, name, backgroundImage}) => ({value: id, label: name, style: {backgroundImage}}))
    const { SelectField } = this
    return (
      <SelectField classes={{menu: styles.themeSelect}} attribute="themeId" label="Theme" options={options}/>
    )
  }

  render() {
    const { TextField } = this
    return(
      <div>
        <div className={styles.section}>
          {this.paymentDestination.type === 'wallets' &&
            <Fragment>
              <Typography variant='h6' className={styles.sectionTitle}>Wallet</Typography>
              {this.renderWallet()}
              <div className={styles.actions}>
                {this.canSubmitToPayToX && this.renderCreateWalletButton()}
              </div>
            </Fragment>
          }
          {this.paymentDestination.type === 'bankAccounts' &&
          <Fragment>
            <Typography variant='h6' className={styles.sectionTitle}>Bank Account</Typography>
            <TextField attribute='paymentDestination.bankName' label='Bank Name'/>
            <TextField attribute='paymentDestination.bankAddress' label='Bank Address'/>
            <TextField attribute='paymentDestination.swiftCode' label='SWIFT Code'/>
            <TextField attribute='paymentDestination.accountName' label='Account Name'/>
            <TextField attribute='paymentDestination.accountBsb' label='Account BSB'/>
            <TextField attribute='paymentDestination.accountNumber' label='Account Number'/>
            <TextField attribute='paymentDestination.payeeName' label='Payee Name'/>
            <TextField attribute='paymentDestination.payeeAddress' label='Payee Address'/>
          </Fragment>
          }
        </div>
        <div className={styles.section}>
          <Typography variant='h6' className={styles.sectionTitle}>Deal Sheet visibility</Typography>
          { this.renderPublicSwitch() }
          <Button variant="contained" color="primary" onClick={() => this.props.history.push(`viewers`)}>Access Control</Button>
          { this.renderThemeSelector() }
        </div>
        <div className={styles.actions}>
          {
            this.canSave &&
            <Button onClick={this.save} className={styles.action} variant='contained' color='primary'>Save</Button>
          }
        </div>
      </div>
    )
  }
}

const styles = StyleSheet({
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start'
  },
  walletStatus: {
    margin: '10px 0',
    padding: '15px',
    backgroundColor: '#efefef',
  },
  sectionTitle: {
    fontWeight: 900,
    fontSize: '24px'
  },
  body: {
    fontFamily: 'Montserrat,sans-serif',
    fontSize: '15px',
    fontWeight: 500,
  },
  section: {
    marginBottom: '40px'
  },
  subheading: {
    fontFamily: 'Montserrat,sans-serif',
    fontWeight: 'bold',
    fontSize: '18px'
  },
  themeSelect: {
    '& span': {
      color: 'white'
    },
    '& li:hover': {
      opacity: 0.9,
      '& span': {
        color: '#DDD'
      }
    }
  }
})

export default connect(({themes}) => ({themes: themes.themes}))(withRouter(Admin))